/**
 * MaReelLoop: the endless, continuously resizing reel slider behind "Our community" (ma-ugc-v2)
 * and the PDP reel arch (ma-media-arch-v2).
 *
 * The slider has one continuous position (pos = 3.4 means 40% of the way from card 3 to card 4).
 * Every frame each card's size and x come from its exact distance to the centre (d), so cards grow
 * and shrink *while* they slide, whether that's a drag, a swipe, a trackpad scroll or auto-advance.
 *
 *   widths[k]  card width at distance k from the centre (piecewise linear in between, last value repeats)
 *   x          the card nearest the centre is the anchor; the rest are laid outward with their real widths,
 *              so every gap is exactly `gap` in every frame
 *
 * It's an endless loop: pos is unbounded, distances wrap round, clicks take the shortest way round,
 * and a card fades out before it wraps to the other side, so nothing ever pops.
 *
 * Options: measure() -> { widths: [px...], gap: px }, render(el, d, u) for section-specific styling
 * (u = |d|), onIndex(i, prev) when the nearest card changes, onSettle(i) when motion stops, start.
 * Returns { goTo(i), next(), index(), busy(), refresh() }.
 */
(function () {
  if (window.MaReelLoop) return;
  const inOut = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);
  const out = (t) => 1 - Math.pow(1 - t, 3);

  window.MaReelLoop = function (stage, items, o) {
    const n = items.length;
    const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
    let pos = o.start || 0, widths = [260], gap = 16, raf = 0, moving = false, dragging = false, idx = -1;

    const wrap = (d) => { d = ((d % n) + n) % n; return d >= n / 2 ? d - n : d; };
    const wAt = (u) => {
      const L = widths.length - 1, k = Math.floor(u), f = u - k;
      const a = widths[Math.min(k, L)], b = widths[Math.min(k + 1, L)];
      return a + (b - a) * f;
    };
    const step = () => (widths[1] || widths[0]) + gap; // drag distance that moves one card

    function measure() { const m = o.measure(); widths = m.widths; gap = m.gap; }

    function render() {
      const c = stage.clientWidth / 2, half = n / 2;
      // Anchor the card nearest the centre (moves linearly, continuous across anchor changes), then lay
      // the others outward with their real widths, so every gap is exactly `gap` in every frame.
      const cards = items.map((el, j) => { const d = wrap(j - pos); return { el, d, w: wAt(Math.abs(d)) }; });
      const sorted = cards.slice().sort((a, b) => a.d - b.d);
      const ai = sorted.reduce((best, k, i) => (Math.abs(k.d) < Math.abs(sorted[best].d) ? i : best), 0);
      const a = sorted[ai];
      a.cx = c + a.d * (wAt(0.5) + gap);
      for (let i = ai + 1; i < sorted.length; i++) sorted[i].cx = sorted[i - 1].cx + sorted[i - 1].w / 2 + gap + sorted[i].w / 2;
      for (let i = ai - 1; i >= 0; i--) sorted[i].cx = sorted[i + 1].cx - sorted[i + 1].w / 2 - gap - sorted[i].w / 2;
      cards.forEach(({ el, d, w, cx }) => {
        const u = Math.abs(d), x = cx - w / 2;
        el.style.width = w + 'px';
        el.style.transform = `translate3d(${x.toFixed(2)}px,0,0)`;
        el.style.zIndex = String(100 - Math.round(u * 10));
        // Fade out in the last half-card before the wrap point, so the jump to the other side is invisible.
        const fade = n > 2 ? Math.max(0, Math.min(1, (half - u) / 0.5)) : 1;
        el.style.opacity = fade < 1 ? fade.toFixed(3) : '';
        el.style.visibility = fade <= 0 ? 'hidden' : '';
        o.render(el, d, u);
      });
      const i = ((Math.round(pos) % n) + n) % n;
      if (i !== idx) { const prev = idx; idx = i; o.onIndex && o.onIndex(i, prev); }
    }

    function stop() { cancelAnimationFrame(raf); moving = false; stage.classList.remove('is-moving'); }
    function animateTo(target, dur, ease) {
      stop();
      if (reduce || !dur || Math.abs(target - pos) < 0.001) { pos = target; render(); o.onSettle && o.onSettle(idx); return; }
      const from = pos, t0 = performance.now();
      moving = true; stage.classList.add('is-moving');
      const tick = (now) => {
        const p = Math.min(1, (now - t0) / dur);
        pos = from + (target - from) * ease(p);
        render();
        if (p < 1) raf = requestAnimationFrame(tick);
        else { moving = false; stage.classList.remove('is-moving'); o.onSettle && o.onSettle(idx); }
      };
      raf = requestAnimationFrame(tick);
    }
    const durFor = (cards) => Math.min(1400, 650 + 200 * Math.max(0, Math.abs(cards) - 1));
    const settle = (ease) => { const t = Math.round(pos); animateTo(t, 420 + 160 * Math.min(1, Math.abs(t - pos)), ease || out); };

    // Drag (mouse) / swipe (touch, pen). Vertical swipes still scroll the page (touch-action: pan-y in CSS).
    let down = false, dragged = false, sx = 0, sy = 0, pid = 0, startPos = 0, samples = [];
    stage.addEventListener('dragstart', (e) => e.preventDefault());
    stage.addEventListener('pointerdown', (e) => {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      down = true; dragged = false; sx = e.clientX; sy = e.clientY; pid = e.pointerId;
    });
    stage.addEventListener('pointermove', (e) => {
      if (!down || e.pointerId !== pid) return;
      if (!dragged) {
        const dx = e.clientX - sx, dy = e.clientY - sy;
        if (Math.abs(dx) < 6 || Math.abs(dx) < Math.abs(dy)) return; // not (yet) a sideways drag
        dragged = true; dragging = true; stop();
        stage.setPointerCapture(pid);
        stage.classList.add('is-dragging');
        sx = e.clientX; startPos = pos; samples = [];
      }
      pos = startPos - (e.clientX - sx) / step();
      samples.push([e.timeStamp, e.clientX]);
      if (samples.length > 6) samples.shift();
      render();
    });
    const release = (e) => {
      if (!down || (e && e.pointerId !== pid)) return;
      down = false;
      if (!dragged) return;
      dragging = false;
      stage.classList.remove('is-dragging');
      // Flick: carry on with the release velocity, then settle on a whole card (at most 3 cards further).
      let v = 0;
      if (samples.length > 1) {
        const a = samples[0], b = samples[samples.length - 1];
        if (b[0] - a[0] > 0 && performance.now() - b[0] < 120) v = (b[1] - a[1]) / (b[0] - a[0]); // px per ms
      }
      const projected = pos - (v * 220) / step();
      const target = Math.round(Math.max(pos - 3, Math.min(pos + 3, projected)));
      animateTo(target, 380 + 170 * Math.min(3, Math.abs(target - pos)), out);
    };
    stage.addEventListener('pointerup', release);
    stage.addEventListener('pointercancel', release);
    // A drag must not also count as a click on a card or a link.
    stage.addEventListener('click', (e) => { if (dragged) { e.preventDefault(); e.stopPropagation(); dragged = false; } }, true);

    // Trackpad / shift+wheel scrolling moves the slider continuously, then settles.
    let wt = 0;
    stage.addEventListener('wheel', (e) => {
      const dx = Math.abs(e.deltaX) > Math.abs(e.deltaY) ? e.deltaX : e.shiftKey ? e.deltaY : 0;
      if (!dx) return; // vertical wheel scrolls the page as normal
      e.preventDefault();
      stop();
      pos += dx / step();
      render();
      clearTimeout(wt);
      wt = setTimeout(() => settle(out), 140);
    }, { passive: false });

    let rz = 0;
    addEventListener('resize', () => { clearTimeout(rz); rz = setTimeout(() => { measure(); render(); }, 80); });

    measure();
    render();
    o.onSettle && o.onSettle(idx);

    return {
      goTo(j) {
        const base = Math.round(pos);
        const delta = wrap(j - base);
        animateTo(base + delta, durFor(delta), inOut);
      },
      next() { animateTo(Math.round(pos) + 1, 900, inOut); },
      index: () => idx,
      busy: () => moving || dragging,
      refresh() { measure(); render(); },
    };
  };
  document.dispatchEvent(new CustomEvent('ma-reel-loop:ready'));
})();
